import { FC } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'
import { Text } from '@/atoms/Text'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { useTranslate } from '@/utils/translate/translate-client'
import { CurrentTheatricalRelease } from '../types'
import { ReleaseCard } from './ReleaseCard'

const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
      staggerChildren: 0.1,
    },
  },
}

const slideVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

const desktopSlideVariants: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

interface Props {
  releases: CurrentTheatricalRelease[]
  className?: string
  containerClassName?: string
  addAnimation?: boolean
  showGiftPifModalExperiment?: 'none' | 'text' | 'image'
  source: 'tickets' | 'landing' | 'default'
}

export const CurrentReleases: FC<Props> = ({
  releases,
  className,
  addAnimation = false,
  source = 'tickets',
  containerClassName,
}) => {
  const { t } = useTranslate('tickets')
  const isDesktop = useMediaQuery({ minWidth: 768 })

  return (
    <motion.section
      {...(addAnimation && {
        initial: 'hidden',
        whileInView: 'visible',
        viewport: { once: true, amount: 0.2 },
        variants: sectionVariants,
      })}
      className={classNames(
        'flex flex-col z-10 relative',
        source === 'landing' ? 'gap-8' : 'gap-4 md:gap-10',
        className,
      )}
    >
      <Text
        className={classNames(
          'photon-title-md',
          source === 'landing' ? 'photon-title-md' : 'md:photon-heading-lg xl:photon-heading-xl',
          source !== 'landing' && styles.layoutContainer,
          containerClassName,
        )}
        weight="bold"
        color="white"
      >
        {t('upNextInTheaters', 'Up Next in Theaters')}
      </Text>

      <EmblaCarousel
        includeArrows
        rightArrowClassname="hidden md:block top-[140px] lg:top-[182px]"
        leftArrowClassname="hidden md:block top-[140px] lg:top-[182px]"
        options={{
          active: false,
          breakpoints: {
            '(min-width: 768px)': {
              active: true,
              dragFree: true,
              align: 'start',
              containScroll: 'trimSnaps',
              slidesToScroll: 1,
            },
          },
        }}
        containerClassName={classNames(
          'flex flex-col gap-10 md:gap-6 lg:gap-8 md:flex-row',
          source !== 'landing' && styles.layoutContainer,
          containerClassName,
        )}
      >
        {releases.map((release, idx) => {
          return (
            <EmblaSlide
              fadeOutOfView
              className={classNames('w-full md:max-w-[480px] xl:max-w-[630px]', {
                'md:mr-6 lg:mr-10': idx === releases.length - 1,
              })}
              key={release.theatricalSlug}
            >
              <motion.div variants={isDesktop ? desktopSlideVariants : slideVariants}>
                <ReleaseCard release={release} source={source} />
              </motion.div>
            </EmblaSlide>
          )
        })}
      </EmblaCarousel>
    </motion.section>
  )
}
